import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';

// ========================================
// 환경 변수 로드
// ========================================

// 환경 구분
const isProduction = process.env.REACT_APP_ENV === 'production';
const isDevelopment = process.env.REACT_APP_ENV === 'development';

// 에뮬레이터 사용 여부
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';

// 데모 모드 감지: 환경 변수가 설정되지 않았거나 demo-api-key인 경우
const isDemo =
  !process.env.REACT_APP_FIREBASE_API_KEY ||
  process.env.REACT_APP_FIREBASE_API_KEY === 'demo-api-key';

// ========================================
// Firebase 설정 정보
// ========================================

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'demo.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'demo-project',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'demo.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || 'demo-app-id'
};

// ========================================
// Firebase 초기화
// ========================================

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let auth: Auth | null = null;
let functions: Functions | null = null;
let storage: FirebaseStorage | null = null;

if (isDemo) {
  // 데모 모드: Firebase를 초기화하지 않음
  console.log('🎭 Running in DEMO mode - Firebase is disabled');
  console.log('To enable Firebase, set REACT_APP_FIREBASE_API_KEY in .env file');
} else {
  // 실제 Firebase 초기화
  try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    functions = getFunctions(app, 'asia-northeast3');
    storage = getStorage(app);

    // ========================================
    // Firebase Emulator 연결 (개발 환경)
    // ========================================
    if (useEmulator) {
      console.log('🔧 Connecting to Firebase Emulators...');

      // Firestore Emulator 연결
      const firestoreHost = process.env.REACT_APP_EMULATOR_FIRESTORE_HOST || '127.0.0.1';
      const firestorePort = parseInt(process.env.REACT_APP_EMULATOR_FIRESTORE_PORT || '8080', 10);
      connectFirestoreEmulator(db, firestoreHost, firestorePort);
      console.log(`  ✅ Firestore Emulator: ${firestoreHost}:${firestorePort}`);

      // Auth Emulator 연결
      const authUrl = process.env.REACT_APP_EMULATOR_AUTH_URL || 'http://127.0.0.1:9099';
      connectAuthEmulator(auth, authUrl, { disableWarnings: true });
      console.log(`  ✅ Auth Emulator: ${authUrl}`);

      // Functions Emulator 연결
      const functionsHost = process.env.REACT_APP_EMULATOR_FUNCTIONS_HOST || '127.0.0.1';
      const functionsPort = parseInt(process.env.REACT_APP_EMULATOR_FUNCTIONS_PORT || '5001', 10);
      connectFunctionsEmulator(functions, functionsHost, functionsPort);
      console.log(`  ✅ Functions Emulator: ${functionsHost}:${functionsPort}`);

      // Storage Emulator 연결
      const storageHost = process.env.REACT_APP_EMULATOR_STORAGE_HOST || '127.0.0.1';
      const storagePort = parseInt(process.env.REACT_APP_EMULATOR_STORAGE_PORT || '9199', 10);
      connectStorageEmulator(storage, storageHost, storagePort);
      console.log(`  ✅ Storage Emulator: ${storageHost}:${storagePort}`);

      console.log('🎉 All emulators connected successfully!');
    } else {
      }
  } catch (error) {
    console.error('❌ Firebase initialization failed:', error);
    console.warn('Falling back to demo mode');
  }
}

// ========================================
// V233: 개발환경 전용 디버그 전역 노출 (DevTools 콘솔)
// ========================================
if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
  if (auth) {
    (window as any).__DEBUG_AUTH__ = auth;
  }
  if (functions) {
    (window as any).__DEBUG_FUNCTIONS__ = functions;
  }
}

// ========================================
// Export
// ========================================

// Firestore 데이터베이스 인스턴스
export { db };

// Firebase Authentication 인스턴스
export { auth };

// Firebase Functions 인스턴스
export { functions };

// Firebase Storage 인스턴스 (V55: 직원 사진 업로드용)
export { storage };

// 환경 플래그
export { isDemo, useEmulator, isDevelopment, isProduction };

export default app;
